import React, { useEffect, useMemo, useRef, useState } from "react";

import moment from "moment";

// Interface
import { type MultiRunSequence, type MultiSetProp } from "../common/commonV4Interface";
import { AlertV4, ButtonV4, ChipV4 } from "../common/ui";
import { BLC_STATUS, type PickMainState } from "react-lib/apps/HISV3/common/CommonInterface";
import { type OnEventHandler } from "react-lib/appcon/common/SearchBoxDropdown";
import { formatDate } from "react-lib/utils/dateUtils";
import {
  ACTIONS,
  BTN_ACTS,
  SEQUENCE_NAME,
  type EncounterType,
  type RunSequence,
  type SetProp,
  type State,
} from "../common/sequence/PatientOrdersV4";
import {
  ACTIONS as SHEET_ACTIONS,
  BTN_ACTS as SHEET_BTN_ACTS,
  SEQUENCE_NAME as SHEET_SEQUENCE,
  SHEET_KINDS,
  sheetErrorKey,
  type AdrChoice,
  type EditableKind,
  type OrderMasterOptions,
  type SaveSetResult,
  type SetEntry,
  type SheetKind,
  type SheetLine as SheetDraftLine,
  type State as OrderSheetState,
} from "../common/sequence/OrderSheetV4";
import {
  ACTIONS as APPOINTMENT_ACTIONS,
  SEQUENCE_NAME as APPOINTMENT_SEQUENCE,
  type State as OrderAppointmentState,
} from "../common/sequence/OrderAppointmentV4";
import { errorTextOf } from "../common/errorTextV4";
import {
  CATEGORY_LABELS,
  CATEGORY_TONES,
  allocatePackages,
  cancelKeyOf,
  codeOf,
  formatPickPrice,
  formatTime,
  getLogRows,
  getReviewItems,
  isLogKind,
  isPendingLine,
  SEARCH_MAX_RESULTS,
  SEARCH_MIN_QUERY,
  sheetGroupOf,
  type DoneRow,
  type LabTest,
  type PickItem,
  type SheetCategory,
} from "./ordersModelV4";
import CardOrderAppointmentV4 from "./CardOrderAppointmentV4";
import { providerIdsOf } from "../common/oaProvidersV4";
import { useOAProvidersV4 } from "../common/useOAProvidersV4";
import ModalCancelOrderV4 from "./ModalCancelOrderV4";
import ModalOrderLogV4 from "./ModalOrderLogV4";
import ModalOrderSetV4, { SET_TABS, type SetTab } from "./ModalOrderSetV4";
import ModalAdrAlertV4 from "./ModalAdrAlertV4";
import ModalDrugInteractionV4 from "./ModalDrugInteractionV4";
import ModalOrderReviewV4 from "./ModalOrderReviewV4";
import OrderDoneTableV4 from "./OrderDoneTableV4";
import OrderSheetSearchV4 from "./OrderSheetSearchV4";
import {
  confirmedGroupsOf,
  doneGroupsOf,
  doneRowsOf,
  sheetGroupsOf,
  summaryTextOf,
  toOrderRow,
} from "./orderListModelV4";
import OrderPendingSheetV4, { type SaveSetForm } from "./OrderPendingSheetV4";
import { sheetMissing } from "./orderSheetLineModelV4";

import { type State as OrderCentralLabState } from "react-lib/apps/HISV3/LAB/sequence/OrderCentralLab";
import { type OrderSetTemplateSequence } from "react-lib/apps/HISV3/common/sequence/OrderSetTemplate";

// Types
type CardOrdersV4Props = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey"
> & {
  masterOptions?: OrderMasterOptions;
  onEvent?: OnEventHandler;
  setProp?: MultiSetProp<SetProp, "OrderCentralLabSequence" | "OrderSheetV4Sequence">;
  runSequence: MultiRunSequence<
    RunSequence,
    | typeof LAB_SEQUENCE
    | typeof ORDER_SET_SEQUENCE
    | typeof SHEET_SEQUENCE
    | typeof APPOINTMENT_SEQUENCE
  >;
  OrderCentralLabSequence?: OrderCentralLabState["OrderCentralLabSequence"];
  OrderSetTemplateSequence?: OrderSetTemplateSequence;
  OrderSheetV4Sequence?: OrderSheetState["OrderSheetV4Sequence"];
  OrderAppointmentV4Sequence?: OrderAppointmentState["OrderAppointmentV4Sequence"];
  PatientOrdersV4Sequence?: State["PatientOrdersV4Sequence"];
  doctorId?: number;
  emrId?: number;
  encounter: EncounterType;
  encounterNumber: string;
  patient: { hn: string; name: string; phone: string };
  divisionName: string;
  apiToken?: string;
  divisionId?: number;
  isDoctor?: boolean;
  onGoAppointments?: () => void;
  onAppointmentChanged?: () => void;
};

type Panel = "" | "sets" | "favs";

type SearchTarget = "sheet" | "advance";

// Const
const LAB_SEQUENCE = "OrderCentralLab";

const LAB_CARD_KEY = "CardOrdersV4Lab";

const ORDER_SET_SEQUENCE = "OrderSetTemplate";

const ORDER_SET_CARD_KEY = "CardOrdersV4OrderSet";

const FAV_PLACEHOLDER = "+ เพิ่มรายการโปรด…";

const CardOrdersV4 = (props: CardOrdersV4Props) => {
  const [openGroups, setOpenGroups] = useState<Partial<Record<SheetCategory, boolean>>>({});
  const [cancelRow, setCancelRow] = useState<DoneRow | null>(null);
  const [logRow, setLogRow] = useState<DoneRow | null>(null);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<SheetCategory | "all">("all");
  const [flash, setFlash] = useState("");
  const [notice, setNotice] = useState<{
    text: string;
    key?: string;
    category?: SheetCategory;
  } | null>(null);
  const [openLines, setOpenLines] = useState<Record<string, boolean>>({});
  const [labTestInfo, setLabTestInfo] = useState<Record<string, LabTest>>({});
  const [pendingCode, setPendingCode] = useState("");
  const [panel, setPanel] = useState<Panel>("");
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [setTab, setSetTab] = useState<SetTab>("all");
  const [openSetId, setOpenSetId] = useState<number | null>(null);
  const [setExcluded, setSetExcluded] = useState<Record<string, boolean>>({});
  const [setRemoved, setSetRemoved] = useState<Record<number, string[]>>({});
  const [saveSet, setSaveSet] = useState<SaveSetForm>({
    open: false,
    name: "",
    scope: "mine",
  });
  const [favourites, setFavourites] = useState<PickItem[]>([]);
  const [searchTarget, setSearchTarget] = useState<SearchTarget>("sheet");

  const sheetKeysRef = useRef<string[]>([]);
  const icd9TimerRef = useRef<ReturnType<typeof setTimeout>>();
  const setFlashRef = useRef("");
  const confirmRef = useRef<{
    keys: string[];
    edited: number;
    cancelled: number;
    pkgCount: number;
  } | null>(null);

  // Memo
  const sequence = props.PatientOrdersV4Sequence;

  const draftItems: any[] = props.OrderCentralLabSequence?.labOrder?.order_items ?? [];

  const labEditId: number | undefined = props.OrderCentralLabSequence?.labOrder?.id;

  const draftKey = JSON.stringify(draftItems);

  const sheetLines: SheetDraftLine[] = props.OrderSheetV4Sequence?.lines ?? [];

  const sheetEditing = props.OrderSheetV4Sequence?.editing ?? [];

  const editingOrderIds = useMemo(
    () =>
      new Set([
        ...sheetEditing.filter((item) => item.kind !== "df").map((item) => item.orderId),
        labEditId,
      ]),
    [sheetEditing, labEditId],
  );

  const editingFeeIds = useMemo(
    () => new Set(sheetEditing.filter((item) => item.kind === "df").map((item) => item.orderId)),
    [sheetEditing],
  );

  const doctorOptions = props.OrderSheetV4Sequence?.doctorOptions ?? [];
  const providers = useOAProvidersV4(props.apiToken, props.divisionId);
  const eligibleDoctorOptions =
    props.apiToken === undefined
      ? doctorOptions
      : providers.doctors
          .filter((doctor) => doctor.employee)
          .map((doctor) => ({
            id: doctor.id,
            employee: doctor.employee!,
            name: doctor.full_name || doctor.name_code || "",
            providers: doctor.provider ?? [],
          }));
  const eligibleProviderIds =
    props.apiToken === undefined ? undefined : providerIdsOf(providers.doctors);
  const invalidDF = sheetLines.some(
    (line) =>
      line.kind === "df" &&
      !eligibleDoctorOptions.some((doctor) => String(doctor.employee) === line.fields.employee),
  );

  const lastConfirmed = sequence?.lastConfirmed;

  const lastOrderBy = doctorOptions.find((doctor) => doctor.id === lastConfirmed?.by)?.name;

  const savedText = lastConfirmed
    ? `ยืนยันคำสั่งล่าสุด ${formatTime(lastConfirmed.time)}${lastOrderBy ? ` โดย ${lastOrderBy}` : ""}`
    : "ยังไม่ได้ยืนยันคำสั่ง";

  const packages = props.OrderSheetV4Sequence?.packages ?? [];

  const pkgUse = useMemo(
    () =>
      allocatePackages(
        [
          ...draftItems.map((item) => ({
            key: `draft_${item.lab_code}`,
            product: Number(item.product) || null,
            quantity: 1,
          })),
          ...sheetLines.map((line) => ({
            key: line.key,
            product: line.product,
            quantity: line.quantity,
          })),
        ],
        packages,
      ),
    [draftKey, sheetLines, packages],
  );

  const sheetErrors = SHEET_KINDS.map(
    (kind) => [kind, props.errorMessage?.[sheetErrorKey(kind)]] as const,
  ).filter(([, error]) => !!error);

  const labOrders = useMemo(
    () => new Map((sequence?.labOrderList ?? []).map((item) => [item.id, item])),
    [sequence?.labOrderList],
  );

  const queueOrders = useMemo(
    () => new Map((sequence?.encounter?.doctor_orders ?? []).map((item) => [item.id, item])),
    [sequence?.encounter?.doctor_orders],
  );

  const orderRows = useMemo(
    () => (sequence?.orderList ?? []).map((order) => toOrderRow(order, labOrders, queueOrders)),
    [sequence?.orderList, labOrders, queueOrders],
  );

  const groups = useMemo(
    () =>
      sheetGroupsOf({
        orderRows,
        editingOrderIds,
        editingFeeIds,
        draftItems,
        labTestInfo,
        sheetLines,
        doctorOptions,
        masterOptions: props.masterOptions,
        doctorFeeList: sequence?.doctorFeeList ?? [],
        miscList: sequence?.miscList ?? [],
        pkgUse,
      }),
    [
      orderRows,
      draftKey,
      editingOrderIds,
      editingFeeIds,
      labTestInfo,
      sheetLines,
      doctorOptions,
      props.masterOptions,
      sequence?.doctorFeeList,
      sequence?.miscList,
      pkgUse,
    ],
  );

  const pendingGroups = groups
    .map((group) => sheetGroupOf(group.category, group.lines.filter(isPendingLine)))
    .filter((group) => group.lines.length > 0);
  const pendingCount = pendingGroups.reduce((sum, group) => sum + group.lines.length, 0);
  const pendingTotal = pendingGroups.reduce((sum, group) => sum + group.subtotal, 0);

  const doneRows = doneRowsOf({
    orderRows,
    editingOrderIds,
    editingFeeIds,
    doctorFeeList: sequence?.doctorFeeList ?? [],
    miscList: sequence?.miscList ?? [],
  });

  const doneKeys = doneRows.map((row) => row.key).join();

  const refreshStatus = props.buttonLoadCheck?.[BTN_ACTS.REFRESH];

  const doneGroups = doneGroupsOf(doneRows);

  const confirmedGroups = confirmedGroupsOf(doneGroups);

  const summaryText = summaryTextOf(confirmedGroups, pendingCount);

  const orderLog = sequence?.orderLog;

  const logRows =
    orderLog && logRow?.order?.id === orderLog.orderId
      ? getLogRows(orderLog.kind, orderLog.items)
      : [];

  const error = props.errorMessage?.[SEQUENCE_NAME];

  const labError = props.errorMessage?.[LAB_CARD_KEY];

  const isSaving =
    props.buttonLoadCheck?.[`${LAB_CARD_KEY}_save`] === BLC_STATUS.LOADING ||
    props.buttonLoadCheck?.[`${LAB_CARD_KEY}_edit_save`] === BLC_STATUS.LOADING ||
    props.buttonLoadCheck?.[SHEET_BTN_ACTS.CONFIRM] === BLC_STATUS.LOADING;

  const isSavingSet = props.buttonLoadCheck?.[SHEET_BTN_ACTS.SAVE_SET] === BLC_STATUS.LOADING;

  const queryLength = query.trim().length;

  const hasQuery = queryLength >= SEARCH_MIN_QUERY;

  const isSheetSearch = searchTarget === "sheet";

  const isSearching =
    props.buttonLoadCheck?.[`${LAB_CARD_KEY}_search`] === BLC_STATUS.LOADING ||
    props.buttonLoadCheck?.[SHEET_BTN_ACTS.SEARCH] === BLC_STATUS.LOADING;

  const labResults: PickItem[] =
    hasQuery && ["all", "lab"].includes(categoryFilter)
      ? (props.OrderCentralLabSequence?.labTests ?? []).map((test: LabTest) => ({
          key: `lab_${test.lab_code}`,
          category: "lab",
          name: test.name,
          price: test.price ?? null,
          lab: test,
        }))
      : [];

  const sheetResults: PickItem[] = hasQuery
    ? (props.OrderSheetV4Sequence?.results ?? [])
        .filter((item) => [item.kind, "all"].includes(categoryFilter))
        .map((item) => ({
          key: item.key,
          category: item.kind,
          name: item.name,
          price: item.price,
          item,
        }))
    : [];

  const searchResults = [...labResults, ...sheetResults];

  const searchError = props.errorMessage?.[SHEET_BTN_ACTS.SEARCH];

  const saveSetError = props.errorMessage?.[SHEET_BTN_ACTS.SAVE_SET];

  const noticeText = props.OrderCentralLabSequence?.duplicateLabDetected
    ? (errorTextOf(props.OrderCentralLabSequence.promptMessage) ?? "เกิดข้อผิดพลาด")
    : (notice?.text ?? props.OrderSheetV4Sequence?.groupNotice);

  const orderSets: Record<string, any>[] = props.OrderSetTemplateSequence?.templateList ?? [];

  const setRows = orderSets.filter((item) =>
    setTab === "mine" ? item.type === "P" : setTab === "clinic" ? item.type !== "P" : true,
  );

  const setEntries = (id: number): SetEntry[] =>
    (props.OrderSheetV4Sequence?.sets?.[id] ?? []).filter(
      (entry) => !setRemoved[id]?.includes(entry.key),
    );

  const favKeys = new Set(favourites.map((item) => item.key));

  const favOptions = searchResults.filter((item) => !favKeys.has(item.key));

  const hasDraft = draftItems.length > 0 || sheetLines.length > 0 || sheetEditing.length > 0;

  const incomplete = groups.flatMap((group) =>
    group.lines.filter((line) => line.missing?.length).map((line) => line.name.split(" ")[0]),
  );

  const adrPending = sheetLines
    .filter((line) => line.raw.adr && !line.fields.adrNote)
    .map((line) => line.name.split(" ")[0]);

  const missing = [
    invalidDF ? "เลือกแพทย์ผู้รับ DF ที่เป็นผู้ให้บริการของ OA นี้" : "",
    pendingCount === 0 && sheetEditing.length === 0 ? "เพิ่มรายการอย่างน้อย 1 รายการ" : "",
    incomplete.length ? `กรอกรายละเอียดที่ต้องระบุ: ${incomplete.join(", ")}` : "",
    adrPending.length ? `ติ๊กรับทราบประวัติแพ้ยา: ${adrPending.join(", ")}` : "",
  ].filter(Boolean);

  const isConfirmOff = missing.length > 0 || !hasDraft || isSaving;

  const missText =
    !hasDraft && lastConfirmed
      ? "ยืนยันคำสั่งแล้ว · เพิ่มรายการใหม่เพื่อยืนยันเพิ่มได้"
      : missing.length
        ? `ยังไม่พร้อมยืนยัน: ${missing.join(" · ")}`
        : "พร้อมยืนยันคำสั่ง";

  const adrAlert = props.OrderSheetV4Sequence?.adrAlert ?? null;

  const interactionAlert = props.OrderSheetV4Sequence?.interactionAlert ?? null;

  const reviewItems = getReviewItems(sheetLines);

  const adrOf = (pick: PickItem) =>
    pick.item?.kind === "drug" && pick.item.id
      ? props.OrderSheetV4Sequence?.adrResults?.[pick.item.id]
      : undefined;

  const packageOf = (pick: PickItem) => {
    const product = pick.lab ? Number(pick.lab.product_id) : pick.item?.id;

    return packages.find((item) => item.product === product);
  };

  // Effect
  useEffect(() => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      encounter: props.encounter,
      restart: true,
    });

    props.runSequence({ sequence: SHEET_SEQUENCE, restart: true, encounterId: props.encounter.id });

    props.runSequence({
      sequence: ORDER_SET_SEQUENCE,
      restart: true,
      cardKey: ORDER_SET_CARD_KEY,
    });
  }, [props.encounter.id]);

  useEffect(() => {
    // OrderCentralLab ต้องมี selectedEmr ก่อนเริ่ม
    if (props.emrId) {
      props.runSequence({ sequence: LAB_SEQUENCE, restart: true });
    }
  }, [props.encounter.id, props.emrId]);

  useEffect(() => {
    const ids = (props.OrderSetTemplateSequence?.templateList ?? []).map((item: any) => item.id);

    if (panel === "sets" && ids.length > 0) {
      props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.LOAD_SETS, ids });
    }
  }, [panel, props.OrderSetTemplateSequence?.templateList]);

  useEffect(() => {
    // ล้างเฉพาะเมื่อมีค่าค้าง (ตอนเปิดหน้าไม่แตะ OrderCentralLabSequence)
    if (query || categoryFilter !== "all") {
      handleClearSearch();
    }
  }, [panel]);

  useEffect(() => {
    if (query.trim().length < SEARCH_MIN_QUERY) {
      return;
    }

    const kinds = SHEET_KINDS.filter((kind) => [kind, "all"].includes(categoryFilter));

    const timer = setTimeout(() => {
      if (["all", "lab"].includes(categoryFilter)) {
        props.runSequence({ sequence: LAB_SEQUENCE, action: "search", cardKey: LAB_CARD_KEY });
      }

      if (kinds.length > 0) {
        props.runSequence({
          sequence: SHEET_SEQUENCE,
          action: SHEET_ACTIONS.SEARCH,
          query,
          kinds,
        });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [query, categoryFilter]);

  useEffect(() => {
    const dueAppointments = props.OrderAppointmentV4Sequence?.dueAppointments ?? [];

    if (dueAppointments.length === 0 || !props.emrId) {
      return;
    }

    const orders = dueAppointments.flatMap((item) => (item.extra?.orders ?? []) as SetEntry[]);

    addEntries(
      orders,
      `ดึงคำสั่งล่วงหน้าจากนัด ${dueAppointments.map((item) => item.id).join(", ")}`,
    );
    props.runSequence({
      sequence: APPOINTMENT_SEQUENCE,
      action: APPOINTMENT_ACTIONS.MARK_PULLED,
      ids: dueAppointments.map((item) => Number(item.id)),
    });
  }, [props.OrderAppointmentV4Sequence?.dueAppointments, props.emrId]);

  useEffect(() => {
    const keys = sheetLines.map((line) => line.key);
    const added = keys.filter((key) => !sheetKeysRef.current.includes(key));

    sheetKeysRef.current = keys;

    if (added.length === 0) {
      return;
    }

    confirmRef.current = null;

    if (setFlashRef.current) {
      const missing = sheetLines.filter(
        (line) => added.includes(line.key) && sheetMissing(line).length > 0,
      );

      setOpenLines((lines) => ({
        ...lines,
        ...Object.fromEntries(missing.map((line) => [line.key, true])),
      }));
      setFlash(
        missing.length > 0
          ? `${setFlashRef.current} · เปิดการ์ดรายละเอียดให้กรอกแล้ว`
          : setFlashRef.current,
      );
      setFlashRef.current = "";

      return;
    }

    setOpenLines((lines) => ({ ...lines, ...Object.fromEntries(added.map((key) => [key, true])) }));

    const newCount = sheetLines.filter((line) => added.includes(line.key) && !line.orderId).length;

    if (newCount > 0) {
      setFlash(`เพิ่ม ${newCount} รายการเข้าใบคำสั่งแล้ว · เปิดการ์ดรายละเอียดให้กรอกแล้ว`);
    }
  }, [sheetLines]);

  useEffect(() => {
    if (!pendingCode || !draftItems.some((item) => item.lab_code === pendingCode)) {
      return;
    }

    setOpenLines({ ...openLines, [`draft_${pendingCode}`]: true });
    setFlash("เพิ่ม 1 รายการเข้าใบคำสั่งแล้ว · เปิดการ์ดรายละเอียดให้กรอกแล้ว");
    setPendingCode("");
  }, [draftKey, pendingCode]);

  useEffect(() => {
    const base = confirmRef.current;

    if (!base || refreshStatus !== BLC_STATUS.SUCCESS) {
      return;
    }

    const created = doneRows.filter((row) => !base.keys.includes(row.key)).length;

    setFlash(
      [
        `ยืนยันคำสั่งแล้ว · สร้าง Order ใหม่ ${created} รายการ · แก้ไข Order ${base.edited} รายการ`,
        base.cancelled > 0 ? `ยกเลิก Order ${base.cancelled} รายการ (ไม่มีรายการเหลือ)` : "",
        base.pkgCount > 0
          ? `ส่ง ${base.pkgCount} รายการใช้สิทธิ์แพ็กเกจไปที่สรุปค่าใช้จ่ายแล้ว (การเงินตัดสิทธิ์ตอนชำระ)`
          : "",
      ]
        .filter(Boolean)
        .join(" · "),
    );
  }, [refreshStatus, doneKeys]);

  // Handler
  const handleToggleGroup = (category: SheetCategory) => {
    setOpenGroups({ ...openGroups, [category]: openGroups[category] === false });
  };

  const handleEditOrder = (row: DoneRow) => {
    const order = row.order;
    const kind = row.category as EditableKind | "lab";

    if (!order && !row.df) {
      return;
    }

    confirmRef.current = null;
    setFlash(`ดึง ${codeOf(row)} กลับมาแก้ไขในรายการรอยืนยันแล้ว · แก้เสร็จกดยืนยันคำสั่งอีกครั้ง`);
    setOpenGroups({ ...openGroups, [kind]: true });

    if (row.df) {
      props.runSequence({
        sequence: SHEET_SEQUENCE,
        action: SHEET_ACTIONS.LOAD_ORDER,
        kind: "df",
        orderId: Number(row.df.id),
        fee: row.df,
      });

      return;
    }

    if (!order) {
      return;
    }

    const prices: any[] = order.price?.orders ?? [];

    if (kind === "lab") {
      handleCloseError(LAB_CARD_KEY);
      props.runSequence({ sequence: LAB_SEQUENCE, action: "edit", selected: order.id });

      return;
    }

    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.LOAD_ORDER,
      kind,
      orderId: order.id,
      prices: prices.map((item) => Number(item.price_unit || 0)),
      specificType: order.specific_type_code,
    });
  };

  const handleOpenCancel = (row: DoneRow) => {
    setCancelRow(row);
  };

  const handleOpenLog = (row: DoneRow) => {
    setLogRow(row);

    if (row.order?.id !== undefined && isLogKind(row.category)) {
      props.runSequence({
        sequence: SEQUENCE_NAME,
        action: ACTIONS.LOAD_LOG,
        orderId: row.order.id,
        kind: row.category,
      });
    }
  };

  const handleCloseLog = () => {
    handleCloseError(BTN_ACTS.LOAD_LOG);
    setLogRow(null);
  };

  const handleCloseCancel = () => {
    if (cancelRow) {
      handleCloseError(cancelKeyOf(cancelRow));
    }

    setCancelRow(null);
  };

  const handleConfirmCancel = (data: { note: string; password: string }) => {
    if (!cancelRow) {
      return;
    }

    const fee = cancelRow.df ?? cancelRow.misc;

    if (fee?.id !== undefined) {
      props.runSequence({
        sequence: SEQUENCE_NAME,
        action: ACTIONS.CANCEL_FEE,
        btnAction: cancelKeyOf(cancelRow),
        kind: cancelRow.df ? "df" : "misc",
        id: fee.id,
        note: data.note,
        password: data.password,
        onSuccess: () => handleCancelSuccess(cancelRow),
      });
    } else if (cancelRow.order?.id !== undefined) {
      props.runSequence({
        sequence: SEQUENCE_NAME,
        action: ACTIONS.CANCEL,
        btnAction: cancelKeyOf(cancelRow),
        id: cancelRow.order.id,
        note: data.note,
        password: data.password,
        onSuccess: () => handleCancelSuccess(cancelRow),
      });
    }
  };

  const handleCancelSuccess = (row: DoneRow) => {
    const orderId = row.order?.id;

    confirmRef.current = null;
    setCancelRow(null);
    setFlash(`ยกเลิก ${codeOf(row)} แล้ว`);

    if (orderId === undefined) {
      return;
    }

    if (row.category !== "lab") {
      props.runSequence({
        sequence: SHEET_SEQUENCE,
        action: SHEET_ACTIONS.DROP_ORDER,
        kind: row.category as EditableKind,
        orderId,
      });
    } else if (labEditId === orderId) {
      props.setProp?.("OrderCentralLabSequence.labOrder", { order_items: [] });
    }
  };

  const handleCloseError = (key: string) => {
    props.setProp?.(`errorMessage.${key}`, null);
  };

  const handleCloseSaveSetError = () => {
    handleCloseError(SHEET_BTN_ACTS.SAVE_SET);
  };

  const handleEvent: OnEventHandler = (event) => {
    props.onEvent?.(event);
  };

  const handleClearSheetErrors = () => {
    for (const [kind] of sheetErrors) {
      handleCloseError(sheetErrorKey(kind));
    }
  };

  const handleChangeQuery = (value: string, target: SearchTarget = "sheet") => {
    setSearchTarget(target);
    setQuery(value);
    props.setProp?.("OrderCentralLabSequence.labName", value);
  };

  const handleChangeCategory = (value: SheetCategory | "all") => {
    setSearchTarget("sheet");
    setCategoryFilter(value);
  };

  const handleClearSearch = () => {
    handleChangeQuery("");
    setCategoryFilter("all");
  };

  const handleAddItem = (pick: PickItem) => {
    const lines = groups.find((group) => group.category === pick.category)?.lines ?? [];
    const existing = lines.find((line) => line.name === pick.name);

    handleClearSearch();

    if (existing) {
      setFlash("ข้าม 1 รายการที่มีอยู่แล้ว");
      setNotice({
        text: `มี "${pick.name}" ในใบคำสั่งแล้ว · ไม่เพิ่มซ้ำ`,
        key: existing.key,
        category: pick.category,
      });

      return;
    }

    if (pick.lab) {
      handleAddLabTest(pick.lab);
    } else if (pick.item) {
      setNotice(null);
      handleClearSheetErrors();
      props.runSequence({
        sequence: SHEET_SEQUENCE,
        action: SHEET_ACTIONS.ADD_ITEMS,
        entries: [
          {
            key: pick.item.key,
            kind: pick.item.kind,
            name: pick.item.name,
            price: Number(pick.item.price || 0),
            quantity: 1,
            item: pick.item,
            fields: pick.fields,
          },
        ],
        isDoctor: props.isDoctor,
      });
    }
  };

  const handleAddLabTest = (test: LabTest) => {
    setLabTestInfo({ ...labTestInfo, [test.lab_code]: test });
    setPendingCode(test.lab_code);
    setFlash("");
    setNotice(null);
    handleCloseError(LAB_CARD_KEY);

    props.runSequence({
      sequence: LAB_SEQUENCE,
      action: "add",
      cardKey: LAB_CARD_KEY,
      addedItem: test,
    });
  };

  const handleGoDuplicate = () => {
    if (!notice?.key) {
      return;
    }

    setOpenGroups({ ...openGroups, [notice.category ?? "lab"]: true });
    document.getElementById(`hv4-ord-line-${notice.key}`)?.scrollIntoView({ block: "center" });
  };

  const handleCloseNotice = () => {
    setNotice(null);
    props.setProp?.("OrderCentralLabSequence.duplicateLabDetected", false);
    props.setProp?.("OrderSheetV4Sequence.groupNotice", null);
  };

  const handleToggleLine = (key: string) => {
    setOpenLines({ ...openLines, [key]: !openLines[key] });
  };

  const handleChangeItem = (index: number, field: "urgency" | "note", value: string) => {
    props.setProp?.(`OrderCentralLabSequence.labOrder.order_items.${index}.${field}`, value);
  };

  const handleDeleteItem = (index: number) => {
    const items = draftItems.filter((_, i) => i !== index);

    props.setProp?.("OrderCentralLabSequence.labOrder.order_items", items);

    if (items.length === 0) {
      props.setProp?.("OrderCentralLabSequence.labOrder", { order_items: [] });
    }

    props.runSequence({ sequence: LAB_SEQUENCE, action: "delete" });
  };

  const handleToggleSet = (id: number) => {
    setOpenSetId(openSetId === id ? null : id);
    setSetExcluded({});
  };

  const handleRemoveSetEntry = (id: number, key: string) => {
    setSetRemoved({ ...setRemoved, [id]: [...(setRemoved[id] ?? []), key] });
  };

  const handleSaveSet = () => {
    const name = saveSet.name.trim();
    const scope = saveSet.scope;

    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.SAVE_SET,
      name,
      scope,
      doctorId: props.doctorId,
      labItems: draftItems,
      onDone: (result: SaveSetResult) => {
        if (result.missing?.length) {
          setNotice({ text: `บันทึก Order set ไม่ได้: ${result.missing.join(" · ")}` });

          return;
        }

        const skipped = result.skipped ?? [];

        setNotice(null);
        setSaveSet({ open: false, name: "", scope });
        setSetTab(scope);
        setFlash(
          [
            `บันทึก Order set "${name}" (${SET_TABS.find(([key]) => key === scope)?.[1]}) แล้ว · ${result.count} รายการ พร้อมรายละเอียดที่กรอกไว้`,
            skipped.length ? `ไม่ได้เก็บ ${skipped.length} รายการ: ${skipped.join(", ")}` : "",
          ]
            .filter(Boolean)
            .join(" · "),
        );
        props.runSequence({
          sequence: ORDER_SET_SEQUENCE,
          action: "searchTemplate",
          cardKey: ORDER_SET_CARD_KEY,
        });
      },
    });
  };

  const handleAddSet = (id: number, name: string) => {
    addEntries(
      setEntries(id).filter((entry) => !setExcluded[entry.key]),
      `เพิ่มชุด "${name}"`,
    );
  };

  const addEntries = (chosen: SetEntry[], label: string) => {
    const names = new Set<string>();
    const labs: LabTest[] = [];
    const others: SetEntry[] = [];
    let skipped = 0;

    for (const entry of chosen) {
      const category = entry.kind;
      const lines = groups.find((group) => group.category === category)?.lines ?? [];
      const nameKey = `${category}:${entry.name}`;

      if (names.has(nameKey) || lines.some((line) => line.name === entry.name)) {
        skipped++;
        continue;
      }

      names.add(nameKey);

      if (entry.kind === "lab") {
        if (entry.lab) {
          labs.push(entry.lab);
        } else {
          skipped++;
        }
      } else {
        others.push(entry);
      }
    }

    const count = labs.length + others.length;
    const message = [
      count > 0 ? `${label} ${count} รายการเข้าใบคำสั่งแล้ว` : "",
      skipped > 0 ? `ข้าม ${skipped} รายการที่มีอยู่แล้ว` : "",
    ]
      .filter(Boolean)
      .join(" · ");

    setNotice(null);
    setPanel("");
    setOpenSetId(null);
    setSetExcluded({});
    setFlash(message);

    if (labs.length > 0) {
      setLabTestInfo({
        ...labTestInfo,
        ...Object.fromEntries(labs.map((test) => [test.lab_code, test])),
      });
      handleCloseError(LAB_CARD_KEY);
      props.runSequence({ sequence: LAB_SEQUENCE, action: "group_add", items: labs });
    }

    if (others.length <= 0) {
      return;
    }
    setFlashRef.current = message;
    handleClearSheetErrors();
    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.ADD_ITEMS,
      entries: others,
      isDoctor: props.isDoctor,
    });
  };

  const handleResolveAdr = (choice: AdrChoice, reason: string) => {
    if (!adrAlert) {
      return;
    }

    const excluded = adrAlert.warnings.filter(
      (warning) => choice !== "order" || warning.locked,
    ).length;
    const addCount = adrAlert.entries.length - excluded;

    if (choice === "cancel") {
      setFlash("");
      setFlashRef.current = "";
    } else {
      const base =
        setFlashRef.current || (addCount > 0 ? `เพิ่ม ${addCount} รายการเข้าใบคำสั่งแล้ว` : "");
      const message = [base, excluded > 0 ? `ไม่รวมยาที่แพ้ ${excluded} รายการ` : ""]
        .filter(Boolean)
        .join(" · ");

      setFlash(message);
      setFlashRef.current = addCount > 0 ? message : "";
    }

    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.RESOLVE_ADR,
      choice,
      reason,
    });
  };

  const handleResolveInteraction = (choice: "cancel" | "order", reason: string) => {
    if (choice === "cancel") {
      setFlash("");
      setFlashRef.current = "";
    }

    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.RESOLVE_INTERACTION,
      choice,
      reason,
    });
  };

  const handleLoadSets = () => {
    const ids = orderSets.map((item) => item.id);

    if (ids.length > 0) {
      props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.LOAD_SETS, ids });
    }
  };

  const handlePrintAppointment = (appointment: Record<string, any>, language: "TH" | "EN") => {
    const block = appointment.display_info ?? {};

    props.onEvent?.({
      message: "PrintScheduling",
      params: {
        form: "FormAppointmentDetail",
        data: {
          ...appointment,
          date: formatDate(moment(appointment.estimated_at_iso)),
          datetime: `${moment(block.start_datetime_iso).format("HH:mm")}-${moment(block.end_datetime_iso).format("HH:mm")}`,
          doctor_note: appointment.order_note?.match(/^.*[^\n\r]/gm) || "",
          language,
        },
      },
    } as any);
  };

  const handleTogglePanel = (value: Panel) => {
    setPanel(panel === value ? "" : value);
  };

  const handleClosePanel = () => {
    setPanel("");
  };

  const handleChangeSheetLine = (
    key: string,
    data: { quantity?: number; priceUnit?: number; fields?: Record<string, string> },
  ) => {
    props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.EDIT, key, ...data });
  };

  const handleSearchIcd9 = (query: string) => {
    clearTimeout(icd9TimerRef.current);

    icd9TimerRef.current = setTimeout(() => {
      props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.SEARCH_ICD9, query });
    }, 400);
  };

  const handleRemoveSheetLine = (key: string) => {
    props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.REMOVE, key });
  };

  const handleToggleFavourite = (pick?: PickItem) => {
    if (!pick) {
      return;
    }

    setFavourites(
      favKeys.has(pick.key)
        ? favourites.filter((item) => item.key !== pick.key)
        : [...favourites, pick],
    );
  };

  const handleAddFavourite = (key: string) => {
    const pick = favOptions.find((item) => item.key === key);

    if (pick) {
      setFavourites([...favourites, pick]);
      handleChangeQuery("");
    }
  };

  const handleAddFromFavourite = (pick: PickItem) => {
    setPanel("");
    handleAddItem(pick);
  };

  const handleCloseReview = () => {
    setIsReviewOpen(false);
  };

  const handleConfirm = () => {
    if (invalidDF) return;
    if (reviewItems.length > 0 && !isReviewOpen) {
      setIsReviewOpen(true);

      return;
    }

    setIsReviewOpen(false);

    const cancelled = sheetEditing.filter(
      (item) =>
        !sheetLines.some((line) => line.kind === item.kind && line.orderId === item.orderId),
    ).length;

    confirmRef.current = {
      keys: doneRows.map((row) => row.key),
      edited:
        [...editingOrderIds].filter((id) => id !== undefined).length +
        editingFeeIds.size -
        cancelled,
      cancelled,
      pkgCount: pkgUse.size,
    };

    const refresh = () => {
      setFlash("");
      props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.REFRESH });
    };

    if (draftItems.length > 0) {
      props.runSequence({
        sequence: LAB_SEQUENCE,
        action: labEditId ? "edit_save" : "save",
        cardKey: LAB_CARD_KEY,
        onSuccess: () => {
          setOpenLines({});
          refresh();
        },
      });
    }

    if (sheetLines.length > 0 || sheetEditing.length > 0) {
      props.runSequence({
        sequence: SHEET_SEQUENCE,
        action: SHEET_ACTIONS.CONFIRM,
        kinds: SHEET_KINDS,
        onDone: (saved: SheetKind[]) => {
          if (saved.length > 0) {
            refresh();
          }
        },
      });
    }
  };

  // Render
  const renderPickerInput = (label: string, placeholder: string) => (
    <input
      className="hv4-input"
      type="search"
      aria-label={label}
      placeholder={placeholder}
      value={query}
      onChange={(e) => handleChangeQuery(e.target.value)}
    />
  );

  const renderPickerResults = (options: PickItem[], onPick: (key: string) => void) => (
    <>
      {queryLength > 0 && !hasQuery && (
        <div className="hv4-ord-results">
          <div className="hv4-ord-results-more">
            พิมพ์อย่างน้อย {SEARCH_MIN_QUERY} ตัวอักษรเพื่อค้นหา
          </div>
        </div>
      )}
      {hasQuery && (
        <div className="hv4-ord-results">
          {options.slice(0, SEARCH_MAX_RESULTS).map((pick) => (
            <div key={pick.key} className="hv4-ord-result">
              <button type="button" className="hv4-ord-result-btn" onClick={() => onPick(pick.key)}>
                <ChipV4 size="sm" tone={CATEGORY_TONES[pick.category]}>
                  {CATEGORY_LABELS[pick.category]}
                </ChipV4>
                <b className="hv4-ord-result-name">{pick.name}</b>
                <span className="hv4-ord-result-price">{formatPickPrice(pick)}</span>
              </button>
            </div>
          ))}
          {options.length === 0 && !isSearching && (
            <div className="hv4-ord-results-empty">ไม่พบรายการ ลองเปลี่ยนคำค้น</div>
          )}
          {options.length > SEARCH_MAX_RESULTS && (
            <div className="hv4-ord-results-more">
              แสดง {SEARCH_MAX_RESULTS} จาก {options.length} รายการ ·
              พิมพ์ชื่อเพิ่มเพื่อกรองให้แคบลง
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="hv4-ord">
      {providers.error && (
        <AlertV4 tone="danger">
          {providers.error} <button onClick={providers.reload}>ลองใหม่</button>
        </AlertV4>
      )}
      <div className="hv4-page-head">
        <h1 className="hv4-h1">คำสั่ง</h1>
        <span className="hv4-page-sub">
          Encounter {props.encounterNumber} · {savedText}
        </span>
      </div>

      <AlertV4 tone="danger" error={error} onClose={() => handleCloseError(SEQUENCE_NAME)} />

      <section aria-label="สรุปคำสั่ง" className="hv4-ord-summary">
        <span className="hv4-ord-summary-label">สรุปคำสั่ง</span>
        {confirmedGroups.map((group) => (
          <ChipV4 key={group.category} size="sm" tone={CATEGORY_TONES[group.category]}>
            {CATEGORY_LABELS[group.category]} {group.rows.length}
          </ChipV4>
        ))}
        <span className="hv4-ord-summary-text">{summaryText || "ยังไม่มีรายการคำสั่ง"}</span>
      </section>

      <CardOrderAppointmentV4
        eligibleProviderIds={eligibleProviderIds}
        OrderAppointmentV4Sequence={props.OrderAppointmentV4Sequence}
        buttonLoadCheck={props.buttonLoadCheck}
        errorMessage={props.errorMessage}
        masterOptions={props.masterOptions}
        runSequence={props.runSequence}
        setProp={props.setProp}
        encounterId={Number(props.encounter.id)}
        patient={props.patient}
        divisionName={props.divisionName}
        search={{
          query: searchTarget === "advance" ? query : "",
          category: searchTarget === "advance" ? categoryFilter : "all",
          results: searchTarget === "advance" ? searchResults : [],
          isSearching,
          orderSets: orderSets.map((item) => ({ id: item.id, name: item.name })),
          favourites,
          setEntriesOf: setEntries,
          onChangeQuery: (value) => handleChangeQuery(value, "advance"),
          onChangeCategory: (value) => {
            setSearchTarget("advance");
            setCategoryFilter(value);
          },
          onOpenSets: handleLoadSets,
        }}
        onPrint={handlePrintAppointment}
        onGoAppointments={props.onGoAppointments}
        onChanged={props.onAppointmentChanged}
      />

      {doneGroups.length > 0 && (
        <OrderDoneTableV4
          groups={doneGroups}
          onEdit={handleEditOrder}
          onCancel={handleOpenCancel}
          onLog={handleOpenLog}
        />
      )}

      {!!logRow && (
        <ModalOrderLogV4
          row={logRow}
          logRows={logRows}
          isLoading={props.buttonLoadCheck?.[BTN_ACTS.LOAD_LOG] === BLC_STATUS.LOADING}
          error={props.errorMessage?.[BTN_ACTS.LOAD_LOG]}
          onClose={handleCloseLog}
        />
      )}

      {!!cancelRow && (
        <ModalCancelOrderV4
          key={cancelRow.key}
          row={cancelRow}
          busy={props.buttonLoadCheck?.[cancelKeyOf(cancelRow)] === BLC_STATUS.LOADING}
          error={props.errorMessage?.[cancelKeyOf(cancelRow)]}
          onClose={handleCloseCancel}
          onConfirm={handleConfirmCancel}
        />
      )}

      {!!flash && <AlertV4 tone="info">{flash}</AlertV4>}

      {!!noticeText && (
        <AlertV4
          tone="warn"
          actions={
            !!notice?.key &&
            !props.OrderCentralLabSequence?.duplicateLabDetected && (
              <ButtonV4 variant="ghost" size="sm" onClick={handleGoDuplicate}>
                ไปที่รายการเดิม
              </ButtonV4>
            )
          }
          onClose={handleCloseNotice}
        >
          ⚠ {noticeText}
        </AlertV4>
      )}

      <AlertV4 tone="danger" error={labError} onClose={() => handleCloseError(LAB_CARD_KEY)} />

      {sheetErrors.map(([kind, sheetError]) => (
        <AlertV4
          key={kind}
          tone="danger"
          error={sheetError}
          onClose={() => handleCloseError(sheetErrorKey(kind))}
        >
          บันทึก {CATEGORY_LABELS[kind]} ไม่สำเร็จ:{" "}
        </AlertV4>
      ))}

      <OrderSheetSearchV4
        query={isSheetSearch ? query : ""}
        category={isSheetSearch ? categoryFilter : "all"}
        results={searchResults}
        isSearching={isSearching}
        error={searchError}
        panel={panel}
        setCount={orderSets.length}
        favCount={favourites.length}
        favKeys={favKeys}
        adrOf={adrOf}
        packageOf={packageOf}
        onChangeQuery={handleChangeQuery}
        onChangeCategory={handleChangeCategory}
        onTogglePanel={handleTogglePanel}
        onAdd={handleAddItem}
        onToggleFavourite={handleToggleFavourite}
      />

      {!!adrAlert && (
        <ModalAdrAlertV4
          alert={adrAlert}
          reasonOptions={props.masterOptions?.drugAllergyReason ?? []}
          onResolve={handleResolveAdr}
        />
      )}

      {!!interactionAlert && (
        <ModalDrugInteractionV4
          alert={interactionAlert}
          reasonOptions={props.masterOptions?.drugInteractReason ?? []}
          onResolve={handleResolveInteraction}
        />
      )}

      {isReviewOpen && (
        <ModalOrderReviewV4
          items={reviewItems}
          onClose={handleCloseReview}
          onConfirm={handleConfirm}
        />
      )}

      {!!panel && (
        <ModalOrderSetV4
          panel={panel}
          setTab={setTab}
          setRows={setRows}
          openSetId={openSetId}
          setExcluded={setExcluded}
          favourites={favourites}
          favPicker={
            <>
              {renderPickerInput("เพิ่มรายการโปรด", FAV_PLACEHOLDER)}
              {renderPickerResults(favOptions, handleAddFavourite)}
            </>
          }
          setEntriesOf={setEntries}
          onClose={handleClosePanel}
          onChangeTab={setSetTab}
          onToggleSet={handleToggleSet}
          onChangeExcluded={setSetExcluded}
          onRemoveSetEntry={handleRemoveSetEntry}
          onAddSet={handleAddSet}
          onAddFromFavourite={handleAddFromFavourite}
          onRemoveFavourite={handleToggleFavourite}
        />
      )}

      <OrderPendingSheetV4
        groups={pendingGroups}
        draftItems={draftItems}
        labTestInfo={labTestInfo}
        openGroups={openGroups}
        openLines={openLines}
        favKeys={favKeys}
        doctorOptions={eligibleDoctorOptions}
        masterOptions={props.masterOptions}
        icd9Options={props.OrderSheetV4Sequence?.icd9Options ?? []}
        searchedItemListWithKey={props.searchedItemListWithKey}
        hasDone={doneRows.length > 0}
        pendingCount={pendingCount}
        pendingTotal={pendingTotal}
        saveSet={saveSet}
        isSavingSet={isSavingSet}
        saveSetError={saveSetError}
        onEvent={handleEvent}
        onToggleGroup={handleToggleGroup}
        onToggleLine={handleToggleLine}
        onChangeSheetLine={handleChangeSheetLine}
        onRemoveSheetLine={handleRemoveSheetLine}
        onChangeItem={handleChangeItem}
        onDeleteItem={handleDeleteItem}
        onToggleFavourite={handleToggleFavourite}
        onSearchIcd9={handleSearchIcd9}
        onChangeSaveSet={setSaveSet}
        onSaveSet={handleSaveSet}
        onCloseSaveSetError={handleCloseSaveSetError}
      />

      <div className="hv4-ord-confirm">
        <span className={`hv4-ord-confirm-text${missing.length ? " hv4-ord-confirm-miss" : ""}`}>
          {missText}
        </span>
        <ButtonV4 variant="confirm" disabled={isConfirmOff} onClick={handleConfirm}>
          ยืนยันคำสั่ง
        </ButtonV4>
      </div>
    </div>
  );
};

CardOrdersV4.displayName = "CardOrdersV4";

export default React.memo(CardOrdersV4);
